import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ==================== TYPES ====================

export interface Bookmark {
  id: string;
  url: string;
  title?: string | null;
  status: 'draft' | 'archived';
  category: 'uncategorized' | 'job';
  created_at: string;
  updated_at: string;
}

export interface CreateBookmarkRequest {
  url: string;
  title?: string;
  status?: 'draft' | 'archived';
  category?: 'uncategorized' | 'job';
}

export interface UpdateBookmarkRequest {
  url: string;
  title?: string;
  status?: 'draft' | 'archived';
  category?: 'uncategorized' | 'job';
}

// ==================== QUERIES ====================

/**
 * Fetch all bookmarks
 */
export const useBookmarks = () => {
  return useQuery({
    queryKey: ['bookmarks'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks`, { headers });
      if (!res.ok) throw new Error('Failed to fetch bookmarks');
      const data = await res.json();
      return data.data as Bookmark[];
    },
  });
};

/**
 * Fetch a specific bookmark by ID
 */
export const useBookmark = (id: string | undefined) => {
  return useQuery({
    queryKey: ['bookmarks', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch bookmark');
      const data = await res.json();
      return data.data as Bookmark;
    },
    enabled: !!id,
  });
};

// ==================== MUTATIONS ====================

/**
 * Create a new bookmark
 */
export const useCreateBookmark = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateBookmarkRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create bookmark');
      }
      const response = await res.json();
      return response.data as Bookmark;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
};

/**
 * Update an existing bookmark
 */
export const useUpdateBookmark = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateBookmarkRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update bookmark');
      }
      const response = await res.json();
      return response.data as Bookmark;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarks', id] });
    },
  });
};

/**
 * Delete a single bookmark
 */
export const useDeleteBookmark = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete bookmark');
      }
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
};

/**
 * Delete multiple bookmarks at once
 */
export const useBulkDeleteBookmarks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks/bulk-delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete bookmarks');
      }
      const response = await res.json();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
};

/**
 * Archive multiple bookmarks at once
 */
export const useBulkArchiveBookmarks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/bookmarks/bulk-archive`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to archive bookmarks');
      }
      const response = await res.json();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
};
